import { useEffect, useRef, useState } from 'react';
import { BASE_COUNT, formatCount, getTrustCount } from '../lib/trust-count';

const COUNT_UP_DURATION = 1600; // ms
const TICK_INTERVAL = 30_000; // ms — hoe vaak we kijken of de teller is opgelopen

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/**
 * Kaart met het aantal mensen dat via hun organisatie terecht kan bij een
 * vertrouwenspersoon van PuntUit. Staat tussen de andere bewijskaarten
 * (500+ organisaties, 70%+ opgelost via het spreekuur).
 *
 * De statische (geprerenderde) HTML bevat BASE_COUNT, zodat server en client
 * bij hydration hetzelfde renderen. Pas na mount stapt de teller naar de
 * actuele waarde — met een count-up zodra de kaart in beeld komt.
 */
export function TrustCountCard() {
  const [displayed, setDisplayed] = useState(BASE_COUNT);
  const cardRef = useRef<HTMLDivElement>(null);
  const animatedRef = useRef(false);

  useEffect(() => {
    const target = getTrustCount();
    const node = cardRef.current;
    let cleanupFrame: (() => void) | undefined;
    let observer: IntersectionObserver | undefined;

    const runCountUp = () => {
      if (animatedRef.current) return;
      animatedRef.current = true;

      // In een achtergrondtab lopen er geen animatieframes; dan zetten we het
      // getal direct op de juiste waarde in plaats van op de startwaarde.
      if (prefersReducedMotion() || document.visibilityState === 'hidden') {
        setDisplayed(target);
        return;
      }

      // Start net onder de eindwaarde: het getal loopt zichtbaar op zonder
      // te suggereren dat er honderden mensen per seconde bijkomen.
      const from = target - 220;
      const start = performance.now();
      let frame = 0;

      const step = (now: number) => {
        const progress = Math.min((now - start) / COUNT_UP_DURATION, 1);
        setDisplayed(Math.round(from + (target - from) * easeOut(progress)));
        if (progress < 1) frame = requestAnimationFrame(step);
      };

      frame = requestAnimationFrame(step);
      cleanupFrame = () => cancelAnimationFrame(frame);
    };

    if (node && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            runCountUp();
            observer?.disconnect();
          }
        },
        { threshold: 0.35 },
      );
      observer.observe(node);
    } else {
      runCountUp();
    }

    // Blijft de bezoeker langer op de pagina, dan loopt de teller vanzelf mee.
    const interval = window.setInterval(() => {
      if (!animatedRef.current) return;
      setDisplayed((current) => Math.max(current, getTrustCount()));
    }, TICK_INTERVAL);

    return () => {
      observer?.disconnect();
      cleanupFrame?.();
      window.clearInterval(interval);
    };
  }, []);

  return (
    <div ref={cardRef} className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
      <p className="text-4xl font-bold mb-2 text-[#0284c7] tabular-nums">
        <span aria-hidden="true">{formatCount(displayed)}</span>
        <span className="sr-only">{formatCount(displayed)} mensen</span>
      </p>
      <p className="text-gray-700 leading-relaxed">
        mensen kunnen via hun organisatie terecht bij een vertrouwenspersoon van PuntUit.
      </p>
    </div>
  );
}
